import { Button } from "@/components/ui/button";
import { CheckCircle2, MapPin, Calendar, Clock, Package, ExternalLink } from "lucide-react";

export interface PurchaseAcceptedPayload {
  type: "purchase_accepted";
  version: number;
  delivery_method: string;
  total_price: number;
  quantity: number;
  address: string | null;
  date: string | null;
  time: string | null;
  listing_title?: string | null;
}

interface Props {
  payload: PurchaseAcceptedPayload;
  timestamp: string;
}

export function PurchaseAcceptedMessage({ payload, timestamp }: Props) {
  const isPickup = payload.delivery_method === "pickup" || payload.delivery_method === "hämta";
  const dateLabel = isPickup ? "Upphämtningsdatum" : "Leveransdatum";
  const addressLabel = isPickup ? "Upphämtningsadress" : "Leveransadress";

  const formattedDate = payload.date
    ? new Date(payload.date + "T00:00:00").toLocaleDateString("sv-SE", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  const mapsUrl = payload.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(payload.address)}`
    : null;

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-lg border-2 border-primary bg-card p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-primary" />
          <p className="text-sm font-semibold text-foreground">Köpförslag accepterat</p>
        </div>

        {payload.listing_title && (
          <p className="mb-3 text-xs text-muted-foreground">{payload.listing_title}</p>
        )}

        <div className="space-y-2 text-sm">
          <div className="flex items-start gap-2">
            <Package className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Pris</p>
              <p className="font-semibold text-foreground">
                {payload.total_price.toLocaleString("sv-SE")} kr
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  ({payload.quantity} st)
                </span>
              </p>
            </div>
          </div>

          {payload.address && (
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{addressLabel}</p>
                <p className="font-medium text-foreground break-words">{payload.address}</p>
              </div>
            </div>
          )}

          {formattedDate && (
            <div className="flex items-start gap-2">
              <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">{dateLabel}</p>
                <p className="font-medium text-foreground">{formattedDate}</p>
              </div>
            </div>
          )}

          {payload.time && (
            <div className="flex items-start gap-2">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Tid</p>
                <p className="font-medium text-foreground">{payload.time}</p>
              </div>
            </div>
          )}
        </div>

        {isPickup && mapsUrl && (
          <Button
            asChild
            className="mt-4 w-full gap-2"
            variant="default"
          >
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
              Öppna i Google Maps
            </a>
          </Button>
        )}

        <p className="mt-3 text-[10px] text-muted-foreground">
          {new Date(timestamp).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
        </p>
      </div>
    </div>
  );
}

export function parseSystemMessage(content: string): PurchaseAcceptedPayload | null {
  if (!content.startsWith("__SYSTEM__:")) return null;
  try {
    const json = content.slice("__SYSTEM__:".length);
    const parsed = JSON.parse(json);
    if (parsed?.type === "purchase_accepted") return parsed as PurchaseAcceptedPayload;
    return null;
  } catch {
    return null;
  }
}
